import { useEffect, useMemo, useState } from "react";

/**
 * Minimal PWA install helper for modern browsers.
 * - Chrome/Edge/Android: uses beforeinstallprompt
 * - iOS Safari: provides manual instructions (no native prompt)
 */
export function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  const isStandalone = useMemo(() => {
    if (typeof window === "undefined") return false;
    const mql = window.matchMedia?.("(display-mode: standalone)");
    const standaloneByMql = Boolean(mql?.matches);
    const standaloneByIos = Boolean(window.navigator?.standalone);
    return standaloneByMql || standaloneByIos;
  }, []);

  const isIos = useMemo(() => {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    return /iPad|iPhone|iPod/.test(ua);
  }, []);

  useEffect(() => {
    const onBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault?.();
      setDeferredPrompt(e);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
  }, []);

  const supported = useMemo(() => {
    if (isStandalone) return false;
    // Native prompt if event captured OR manual (iOS) instructions
    return Boolean(deferredPrompt) || isIos;
  }, [deferredPrompt, isIos, isStandalone]);

  const install = async () => {
    if (isStandalone) return { outcome: "already-installed" };

    if (deferredPrompt?.prompt) {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      return { outcome: choice?.outcome || "dismissed" };
    }

    if (isIos) {
      return { outcome: "manual-ios" };
    }

    return { outcome: "unsupported" };
  };

  return {
    supported,
    isInstalled: isStandalone,
    isIos,
    install,
  };
}
